import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { X, Upload, File, Image, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackStorageUsage } from "@/services/safeUsageTracking";
import { useAuth } from "@/contexts/AuthContext";

// Constants
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Helper function to convert file to base64
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert file to base64"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

// Helper function to validate file
const validateFile = (file: File, acceptedTypes: string[]): string | null => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return `File "${file.name}" is too large. Maximum size is 50MB.`;
  }

  // Check file type
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!extension || !acceptedTypes.includes(extension)) {
    return `File "${file.name}" has an unsupported format. Only ${acceptedTypes.map((t) => t.toUpperCase()).join(", ")} files are allowed.`;
  }

  return null; // File is valid
};

interface UploadedFile {
  id: string;
  name: string;
  url: string;
  base64?: string; // Add base64 data for OpenAI compatibility
  type: string;
  size: number;
}

interface FileUploadProps {
  onFilesChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  value?: UploadedFile[];
  className?: string;
  mode?: "file" | "image"; // New prop to handle different upload modes
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 5,
  value = [],
  className = "",
  mode = "file",
}) => {
  // Fixed accepted file types based on OpenAI compatibility
  const finalAcceptedTypes =
    mode === "image" ? ["png", "jpg", "jpeg"] : ["pdf"];

  // Ensure value is always an array to prevent .map() errors
  const safeValue = Array.isArray(value) ? value : [];

  const [uploading, setUploading] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const { toast } = useToast();
  const { organization } = useAuth();

  const uploadFile = async (file: File): Promise<UploadedFile | null> => {
    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `${fileId}-${file.name}`;
    const filePath = `assistant-files/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from("agent_files")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("agent_files").getPublicUrl(filePath);

      // Convert file to base64 for OpenAI compatibility
      let base64Data: string | undefined;
      try {
        base64Data = await convertFileToBase64(file);
        console.log(
          `✅ Converted ${file.name} to base64 (${base64Data.length} chars)`,
        );
      } catch (error) {
        console.warn(`⚠️ Failed to convert ${file.name} to base64:`, error);
        // Continue without base64 - fallback to URL
      }

      return {
        id: fileId,
        name: file.name,
        url: publicUrl,
        base64: base64Data, // Include base64 data
        type: file.type,
        size: file.size,
      };
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  const handleFileSelect = async (files: FileList) => {
    const fileArray = Array.from(files);

    // Validate file count
    if (safeValue.length + fileArray.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `You can only upload up to ${maxFiles} files`,
        variant: "destructive",
      });
      return;
    }

    // Validate files (type and size)
    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach((file) => {
      const error = validateFile(file, finalAcceptedTypes);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    // Show validation errors
    if (errors.length > 0) {
      toast({
        title: "Invalid files",
        description: errors.slice(0, 3).join(" "), // Show first 3 errors
        variant: "destructive",
      });
    }

    if (validFiles.length === 0) return;

    // Start uploading
    const uploadingIds = validFiles.map(() =>
      Math.random().toString(36).substr(2, 9),
    );
    setUploading((prev) => [...prev, ...uploadingIds]);

    const uploadPromises = validFiles.map(async (file, index) => {
      const uploadedFile = await uploadFile(file);
      setUploading((prev) => prev.filter((id) => id !== uploadingIds[index]));
      return uploadedFile;
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(
        (file): file is UploadedFile => file !== null,
      );

      if (successfulUploads.length > 0) {
        const newFiles = [...safeValue, ...successfulUploads];
        onFilesChange(newFiles);

        // Track storage usage after successful uploads (non-blocking)
        if (organization?.id) {
          trackStorageUsage(organization.id).catch((error) =>
            console.warn("Failed to track storage usage:", error),
          );
        }

        // Success toast removed per user request
      }

      if (results.some((result) => result === null)) {
        toast({
          title: "Some uploads failed",
          description: "Please try uploading the failed files again",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload files. Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeFile = (fileId: string) => {
    const newFiles = safeValue.filter((file) => file.id !== fileId);
    onFilesChange(newFiles);
  };

  const downloadFile = async (file: UploadedFile) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const getFilePreview = (file: UploadedFile) => {
    if (file.type.startsWith("image/")) {
      return (
        <img
          src={file.url}
          alt={file.name}
          className="h-10 w-10 object-cover rounded"
        />
      );
    }
    return (
      <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
        <File className="h-5 w-5 text-gray-500" />
      </div>
    );
  };

  const cropFileName = (name: string, maxLength: number = 25) => {
    if (name.length <= maxLength) return name;
    const extension = name.split(".").pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf("."));
    const maxNameLength = maxLength - (extension ? extension.length + 4 : 3); // Account for "..." and extension
    return `${nameWithoutExt.substring(0, maxNameLength)}...${extension ? `.${extension}` : ""}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <Card
        className={`border-2 border-dashed transition-colors cursor-pointer bg-[#FAFBFC] ${
          dragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => {
          const input = document.createElement("input");
          input.type = "file";
          input.multiple = true;
          input.accept = finalAcceptedTypes.map((type) => `.${type}`).join(",");
          input.onchange = (e) => {
            const target = e.target as HTMLInputElement;
            if (target.files) {
              handleFileSelect(target.files);
            }
          };
          input.click();
        }}
      >
        <CardContent className="flex items-center justify-center py-6 text-left">
          <div className="flex items-center gap-4">
            {mode === "image" ? (
              <Image className="h-8 w-8 text-gray-400 flex-shrink-0" />
            ) : (
              <Upload className="h-8 w-8 text-gray-400 flex-shrink-0" />
            )}
            <div>
              <p
                className="text-sm font-medium text-gray-900 mb-1"
                style={{ fontSize: "14px" }}
              >
                {mode === "image"
                  ? "Drop images here or click to upload"
                  : "Drop files here or click to upload"}
              </p>
              <p className="text-xs text-gray-500" style={{ fontSize: "12px" }}>
                Upload up to {maxFiles} {mode === "image" ? "images" : "files"}{" "}
                ({finalAcceptedTypes.map((ext) => ext.toUpperCase()).join(", ")}{" "}
                only, max 50MB each)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Indicators */}
      {uploading.length > 0 && (
        <div className="space-y-2">
          {uploading.map((id) => (
            <div key={id} className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">Uploading...</span>
                </div>
                <Progress value={undefined} className="h-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files */}
      {safeValue.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Uploaded Files</h4>
          <div className="grid grid-cols-2 gap-2">
            {safeValue.map((file) => (
              <div
                key={file.id}
                className="relative p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
                onClick={() => downloadFile(file)}
                title="Click to download"
              >
                <div className="flex items-center space-x-3">
                  {getFilePreview(file)}
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-sm font-medium text-gray-900 truncate"
                      title={file.name}
                    >
                      {cropFileName(file.name, 20)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(file.id);
                  }}
                  title="Remove file"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
